/**
 * PATH: apps/hr/src/pages/documents/document-detail/DetailModals.tsx
 * PURPOSE: Reject and Delete confirmation modals for documents
 */

import { useState } from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'

export function RejectModal({ isOpen, onClose, onReject }: {
  isOpen: boolean; onClose: () => void; onReject: (reason: string) => void
}) {
  const [reason, setReason] = useState('')
  const handleSubmit = () => {
    if (!reason.trim()) { alert('Please provide a rejection reason'); return }
    onReject(reason); setReason('')
  }
  if (!isOpen) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-md m-4 p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Reject Document</h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">Please provide a reason for rejecting this document.</p>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Enter rejection reason..."
          className="w-full h-32 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none text-sm" />
        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">Cancel</button>
          <button onClick={handleSubmit} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors">Reject Document</button>
        </div>
      </div>
    </div>
  )
}

export function DeleteModal({ isOpen, onClose, onDelete, isDeleting }: {
  isOpen: boolean; onClose: () => void; onDelete: () => void; isDeleting: boolean
}) {
  if (!isOpen) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-sm m-4 p-6">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Delete Document</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">Are you sure you want to delete this document? This action cannot be undone.</p>
          <div className="flex items-center justify-center gap-3">
            <button onClick={onClose} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">Cancel</button>
            <button onClick={onDelete} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2">
              {isDeleting ? (<><Loader2 className="w-4 h-4 animate-spin" />Deleting...</>) : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
